/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { TextWithLabel as Text } from "@octopusdeploy/design-system-octopus-components";
import type { GenericOidcAccountResource } from "@octopusdeploy/octopus-server-client";
import { AccountType } from "@octopusdeploy/octopus-server-client";
import { cloneDeep } from "lodash";
import React from "react";
import { KeyValueEditList, type KeyValuePair } from "~/components/EditList/KeyValueEditList";
import { AvailableDeploymentSubjectKeys, AvailableExecutionSubjectKeys, AvailableRunbookSubjectKeys, DefaultDeploymentSubjectKeys, generateSampleSubject, OpenIdSubjectMultiSelect, subjectKeySummary, } from "~/components/MultiSelect/OpenIdSubjectMultiSelect";
import { ErrorPanel, ExpandableFormSection, FormSectionHeading, Note, Summary } from "~/components/form";
import type { AccountEditBaseProps, AccountEditModel } from "./AccountEditBase";
import AccountEditBaseDrawer from "./AccountEditBaseDrawer";
import AccountEditBasePaper from "./AccountEditBasePaper";
import styles from "./style.module.less";
interface GenericOidcAuth {
    deploymentSubjectKeys: string[];
    audience: string;
    customClaims: KeyValuePair[];
}
interface GenericOidcModel extends AccountEditModel {
    accountType: AccountType.GenericOidcAccount;
    authentication: GenericOidcAuth;
}
const defaultGenericOidc: GenericOidcAuth = {
    deploymentSubjectKeys: [],
    audience: "api://default",
    customClaims: [],
};
export default function GenericOidcAccountEdit(props: AccountEditBaseProps<GenericOidcAccountResource>) {
    switch (props.type) {
        case "paper":
            return <GenericOidcAccountEditPaper {...props}/>;
        case "drawer":
            return <GenericOidcAccountEditDrawer {...props}/>;
        default:
            return <ErrorPanel message="Invalid layout"/>;
    }
}
class GenericOidcAccountEditPaper extends AccountEditBasePaper<GenericOidcAccountResource, GenericOidcModel> {
    getPartialModel(account?: GenericOidcAccountResource) {
        return getPartialModel(account);
    }
    getPartialResource() {
        return getPartialResource(this.state.model);
    }
    getPageActions() {
        return [];
    }
    getTestDialog() {
        return null;
    }
    customExpandableFormSections() {
        return customExpandableFormSections(this.state.model, (auth) => this.setState((prev) => ({
            model: {
                ...prev.model,
                authentication: {
                    ...prev.model.authentication,
                    ...auth,
                },
            },
        })), this.getFieldError);
    }
}
class GenericOidcAccountEditDrawer extends AccountEditBaseDrawer<GenericOidcAccountResource, GenericOidcModel> {
    getPartialModel(account?: GenericOidcAccountResource) {
        return getPartialModel(account);
    }
    getPartialResource() {
        return getPartialResource(this.state.model);
    }
    getPageActions() {
        return [];
    }
    getTestDialog() {
        return null;
    }
    customExpandableFormSections() {
        return customExpandableFormSections(this.state.model, (auth) => this.setState((prev) => ({
            model: {
                ...prev.model,
                authentication: {
                    ...prev.model.authentication,
                    ...auth,
                },
            },
        })), this.getFieldError);
    }
}
function getPartialModel(account?: GenericOidcAccountResource): Partial<GenericOidcModel> | undefined {
    if (!account) {
        return {
            accountType: AccountType.GenericOidcAccount,
            authentication: cloneDeep(defaultGenericOidc),
        };
    }
    const oidcAccount = account as GenericOidcAccountResource;
    return {
        ...oidcAccount,
        accountType: AccountType.GenericOidcAccount,
        authentication: {
            deploymentSubjectKeys: oidcAccount.DeploymentSubjectKeys,
            audience: oidcAccount.Audience,
            customClaims: Object.entries(oidcAccount.CustomClaims).map(([key, value]) => ({
                key,
                value,
            })),
        },
    };
}
function getPartialResource(modelState: GenericOidcModel): (Partial<GenericOidcAccountResource> & {
    AccountType: AccountType;
}) | undefined {
    const resource = {
        AccountType: modelState.accountType,
    };
    return {
        ...resource,
        Audience: modelState.authentication.audience,
        DeploymentSubjectKeys: modelState.authentication.deploymentSubjectKeys,
        CustomClaims: modelState.authentication.customClaims.reduce((acc, cur) => {
            acc[cur.key] = cur.value;
            return acc;
        }, {} as Record<string, string>),
    };
}
function audienceSummary(model: GenericOidcAuth) {
    return model.audience ? Summary.summary(model.audience) : Summary.placeholder("Federated Identity audience");
}
function customExpandableFormSections(modelState: GenericOidcModel, setOidcAuth: <GenericOidcAuth, K extends keyof GenericOidcAuth>(newState: Pick<GenericOidcAuth, K>) => void, getFieldError: (field: string) => string): React.ReactElement[] {
    const baseElements: React.ReactElement[] = [];
    baseElements.push(<div key="Generic-Oidc">
            <FormSectionHeading title="Subject Claim" key={"SubjectClaim"}/>
            <ExpandableFormSection errorKey="deploymentSubjectKeys" key="deploymentSubjectKeys" title="Deployments & Runbooks" help="Select keys to include for a deployment or runbook." summary={subjectKeySummary(modelState.authentication.deploymentSubjectKeys)}>
                <OpenIdSubjectMultiSelect onChange={(deploymentSubjectKeys) => setOidcAuth({ deploymentSubjectKeys })} value={modelState.authentication.deploymentSubjectKeys} subjectKeys={AvailableExecutionSubjectKeys} accessibleName="Deployment Subject Keys"/>
                <Note>
                    Subject claim formats:
                    <br />
                    <b>Deployment:</b>
                    <span>{generateSampleSubject(modelState.authentication.deploymentSubjectKeys, DefaultDeploymentSubjectKeys, "deployment", AvailableDeploymentSubjectKeys)}</span>
                    <br />
                    <b>Runbook:</b>
                    <span>{generateSampleSubject(modelState.authentication.deploymentSubjectKeys, DefaultDeploymentSubjectKeys, "runbook", AvailableRunbookSubjectKeys)}</span>
                    <br />
                    <span className={styles.secondaryTextColor}>If no keys are specified, the subject claim will default to: {DefaultDeploymentSubjectKeys.join(", ")}. Any keys not selected will not be included in the subject claim.</span>
                </Note>
            </ExpandableFormSection>
            <FormSectionHeading title="Other Claims" key={"OtherClaims"}/>
            <ExpandableFormSection errorKey="Audience" key="audience" title="Audience" summary={audienceSummary(modelState.authentication)} help={`Federated credentials audience, this value is used to establish a connection between external workload identities`}>
                <Text value={modelState.authentication.audience} onChange={(audience) => setOidcAuth({ audience })} label="Audience" error={getFieldError("Audience")}/>
            </ExpandableFormSection>
            <ExpandableFormSection errorKey="customClaims" title="Custom Claims" summary={modelState.authentication.customClaims.length > 0 ? Summary.summary(`${modelState.authentication.customClaims.length} custom claims defined`) : Summary.placeholder("Custom claims to be added to the authentication token")} help={<>
                        <span>Add custom claims to this OIDC account. Supports variable substitution expressions. JSON values will be included as JSON in the authentication token.</span>
                        <br />
                        <b>Warning: Sensitive values should not be passed in custom claims.</b>
                    </>}>
                <KeyValueEditList items={() => modelState.authentication.customClaims} name="Custom Claims" maxKeyValuePairs={10} onChange={(val) => setOidcAuth({ customClaims: val })} valueLabel="Value" valueMultiline={true} keyLabel="Claim" hideBindOnKey={true} hideBindOnValue={true} verticalLayout={true}/>
            </ExpandableFormSection>
        </div>);
    return baseElements;
}
